/*
 * Copyright (c) 2022 automata-network/witness-v2 authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { HTMLAttributes } from 'react'
import { pxToVw } from 'src/functions/units'
import styled, { css } from 'styled-components'

import { Color } from '../../types/theme'

type BaseProps = {
  size: number | `${number}px`
  weight?: 400 | 500 | 'normal'
  color?: Color
}

type TransientProps = {
  [K in keyof BaseProps as `$${K}`]: BaseProps[K]
}

const BaseTypeface = styled.span<TransientProps>`
  font-size: ${({ $size }) => $size && pxToVw($size)};
  font-weight: ${({ $weight }) => $weight};
  color: ${({ $color }) => $color || 'inherit'};
  white-space: pre-line;

  ${({ theme, $size }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw($size, 720)};
    `}
  `}
`

const StyledTypeface = styled(({ size, weight, color, ...rest }) => (
  <BaseTypeface $size={size} $weight={weight} $color={color} {...rest} />
))``

type TypefaceProps = HTMLAttributes<JSX.IntrinsicElements> & Omit<BaseProps, 'size'>

const Typeface = {
  Tiny: ({ weight = 400, color, children, ...rest }: TypefaceProps) => (
    <StyledTypeface size={12} weight={weight} color={color} {...rest}>
      {children}
    </StyledTypeface>
  ),
  Small: ({ weight = 400, color, children, ...rest }: TypefaceProps) => (
    <StyledTypeface size={14} weight={weight} color={color} {...rest}>
      {children}
    </StyledTypeface>
  ),
  Regular: ({ weight = 400, color, children, ...rest }: TypefaceProps) => (
    <StyledTypeface size={16} weight={weight} color={color} {...rest}>
      {children}
    </StyledTypeface>
  ),
  Body: ({ weight = 400, color, children, ...rest }: TypefaceProps) => (
    <StyledTypeface size={18} weight={weight} color={color} {...rest}>
      {children}
    </StyledTypeface>
  ),
  ActionLabel: ({ weight = 400, color, children, ...rest }: TypefaceProps) => (
    <StyledTypeface size={20} weight={weight} color={color} {...rest}>
      {children}
    </StyledTypeface>
  ),
  Heading3: ({ weight = 500, color, children, ...rest }: TypefaceProps) => (
    <StyledTypeface forwardedAs={'h3'} size={24} weight={weight} color={color} {...rest}>
      {children}
    </StyledTypeface>
  ),
  Heading2: ({ weight = 500, color, children, ...rest }: TypefaceProps) => (
    <StyledTypeface forwardedAs={'h2'} size={30} weight={weight} color={color} {...rest}>
      {children}
    </StyledTypeface>
  ),
  Heading1: ({ weight = 500, color, children, ...rest }: TypefaceProps) => (
    <StyledTypeface forwardedAs={'h1'} size={40} weight={weight} color={color} {...rest}>
      {children}
    </StyledTypeface>
  )
}

export default Typeface
